import React from 'react';
import { TrendingUp, Target, Award, Users, CheckCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';

const CmmiServices = () => {
  const navigate = useNavigate();

  const handleScroll = (href) => {
    if (href.startsWith('#')) {
      navigate('/' + href);
    }
  };

  return (
    <section id="cmmi-services" className="pt-20 sm:pt-24 md:pt-32 pb-12 sm:pb-16 md:pb-20 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto mb-16 sm:mb-20">
          <div className="inline-block mb-6">
            <span className="inline-flex items-center gap-2 bg-green-500/20 border border-green-500/40 text-green-300 px-4 py-2 rounded-full font-semibold text-xs uppercase tracking-wider backdrop-blur-sm">
              <TrendingUp className="w-4 h-4" />
              Process Maturity & Improvement
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            CMMI{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500">Services</span>
          </h2>
          <div className="text-base sm:text-lg text-slate-300 leading-relaxed space-y-4">
            <p>
              CMMI provides a structured approach for strengthening organizational capability, improving performance, and aligning operations to business goals.
            </p>
            <p>
              Organizations adopt CMMI for a variety of reasons—improving quality and consistency, increasing predictability, enhancing overall performance, and meeting contractual or regulatory requirements.
            </p>
            <p>
              In some cases, a defined CMMI Maturity Level is required to compete in regulated or government environments. In others, the priority is building processes that genuinely support day-to-day operations and long-term improvement.
            </p>
            <p>
              Our CMMI services are tailored to your organization's needs and support both objectives. We focus on practical, scalable solutions aligned to your goals while remaining consistent with the intent of the CMMI model.
            </p>
          </div>
        </div>

        {/* CMMI Services Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-20">
          <div className="group bg-slate-800/60 backdrop-blur-md border border-white/10 rounded-2xl p-8 hover:bg-slate-800/80 hover:border-green-400/50 hover:shadow-2xl transition-all duration-300">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                <Award className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-green-300 transition-colors">CMMI Appraisals</h3>
                <p className="text-slate-300 leading-relaxed">
                  Organizations gain value by measuring capability and performance through CMMI appraisals. Appraisals identify strengths and weaknesses against CMMI best practices and highlight improvement priorities.
                </p>
              </div>
            </div>
            <p className="text-slate-300 leading-relaxed mb-6">
              We conduct the full range of CMMI appraisals, including:
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-white">Benchmark Appraisals</p>
                  <p className="text-slate-400 text-sm">Determine a CMMI Maturity Level.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-white">Evaluation Appraisals</p>
                  <p className="text-slate-400 text-sm">Identify strengths, gaps, and improvement opportunities.</p>
                </div>
              </div>
            </div>
            <p className="text-slate-300 leading-relaxed mt-6">
              We work closely with leadership and teams to define the most appropriate appraisal type, scope, and model content. Results are presented clearly and constructively to confirm current performance and prioritize the next steps in your improvement journey.
            </p>
          </div>

          <div className="group bg-gradient-to-br from-slate-800/60 to-slate-900/60 backdrop-blur-md border border-green-400/20 rounded-2xl p-8 hover:border-green-400/50 hover:shadow-2xl transition-all duration-300">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-green-400 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-green-300 transition-colors">CMMI Implementation & Consulting</h3>
                <p className="text-slate-300 leading-relaxed">
                  Successful CMMI adoption is not about templates or checklists—it's about aligning practices with how your organization actually works.
                </p>
              </div>
            </div>
            <p className="text-slate-300 leading-relaxed mb-6">
              Our consultants bring hands-on experience supporting organizations of varying sizes, industries, and maturity levels. We take time to understand:
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                <span className="text-slate-300">Your business drivers and constraints</span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                <span className="text-slate-300">What success looks like for your organization</span>
              </div>
              <div className="flex items-start gap-3 sm:col-span-2">
                <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                <span className="text-slate-300">Existing processes, culture, and tools</span>
              </div>
            </div>
            <p className="text-slate-300 leading-relaxed mt-6">
              From there, we design and implement CMMI-aligned solutions that are practical, scalable, and sustainable—supporting both compliance and performance improvement.
            </p>
          </div>

          <div className="group bg-slate-800/60 backdrop-blur-md border border-white/10 rounded-2xl p-8 hover:bg-slate-800/80 hover:border-blue-400/50 hover:shadow-2xl transition-all duration-300">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-green-400 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-green-300 transition-colors">CMMI Training</h3>
                <p className="text-slate-300 leading-relaxed">
                  Understanding the CMMI model is key to using it effectively. Our certified CMMI instructors deliver training that builds foundational knowledge and practical insight.
                </p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                <span className="text-slate-300">Standard CMMI introductory and role-based courses</span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                <span className="text-slate-300">Customized workshops focused on applying CMMI practices in your environment</span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                <span className="text-slate-300">Targeted sessions for leadership, practitioners, and appraisal team members</span>
              </div>
            </div>
            <p className="text-slate-300 leading-relaxed mt-6">
              Training is tailored to your context, helping participants move beyond theory to confident application.
            </p>
          </div>

          <div className="group bg-gradient-to-br from-slate-800/60 to-slate-900/60 backdrop-blur-md border border-blue-400/20 rounded-2xl p-8 hover:border-blue-400/50 hover:shadow-2xl transition-all duration-300">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-green-300 transition-colors">Integrating CMMI with Other Frameworks and Standards</h3>
                <p className="text-slate-300 leading-relaxed">
                  CMMI rarely exists in isolation. If your organization uses additional frameworks or standards—such as CMMC, ISO, or Six Sigma—we help integrate CMMI practices to avoid duplication and confusion.
                </p>
              </div>
            </div>
            <p className="text-slate-300 leading-relaxed">
              Our multi-model approach emphasizes alignment, consistency, and efficiency so teams can operate within a cohesive improvement framework rather than competing ones.
            </p>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-green-700 rounded-3xl p-10 sm:p-16 text-white text-center relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white opacity-5 rounded-full -mr-20 -mt-20"></div>
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-white opacity-5 rounded-full -ml-20 -mb-20"></div>
          <div className="relative z-10">
            <h3 className="text-3xl sm:text-4xl font-bold mb-6">Ready to Apply CMMI Practically?</h3>
            <p className="text-lg text-green-100 mb-8 max-w-2xl mx-auto">
              Let our team help you select the right appraisal path, align practices to real operations, and build sustainable improvement.
            </p>
            <Button
              onClick={() => handleScroll('#contact')}
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-green-50 text-green-700 font-bold px-8 py-4 rounded-xl hover:shadow-xl transition-all duration-300 group"
            >
              Start Your CMMI Journey
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CmmiServices;
